/**
 * Maps CI/CD `pipelineConfig` (CicdConfiguration state) into the same shape as
 * {@link mapGenptScheduleToEditPrefill} so `TestConfigurationForm` / `APIConfigurationForm`
 * can restore Configuration + Throttling when reopening Schedule GenPT with `source="pipeline"`.
 *
 * @param {object} pipeline - Local pipeline state (see CicdConfiguration `INITIAL_PIPELINE_CONFIG`).
 * @param {object} [options]
 * @param {string} [options.targetURL]
 * @param {number|string|null} [options.assetId]
 * @param {string|null} [options.assetExposure] - `'internal' | 'external'` from the asset row
 */
export function mapPipelineConfigToGenptEditPrefill(pipeline, options = {}) {
  if (!pipeline || typeof pipeline !== 'object') return null;

  const { targetURL = '', assetId = null, assetExposure = null } = options;

  const labelToTestType = (label) => {
    if (label == null || String(label).trim() === '') return 'normal';
    const t = String(label).trim();
    const map = {
      Express: 'quick',
      'Site-Wide': 'normal',
      Wordpress: 'wordpress',
      WordPress: 'wordpress',
      // ROAR-4511: Business Scenario as a CI/CD test type — without this the label fell through to
      // 'normal', silently downgrading a restored scenario config to a site-wide test.
      Scenario: 'scenario',
    };
    if (map[t]) return map[t];
    const lower = t.toLowerCase();
    if (lower.includes('wordpress')) return 'wordpress';
    if (lower === 'express' || lower === 'quick') return 'quick';
    if (lower === 'site-wide' || lower === 'site_wide' || lower === 'normal') return 'normal';
    if (lower === 'scenario' || lower === 'business scenario') return 'scenario';
    return 'normal';
  };

  const authRaw = String(pipeline.authentication ?? '').trim();
  const authLower = authRaw.toLowerCase();
  const isUnauthenticated = authLower === 'unauthenticated' || authLower === '';
  const authType = isUnauthenticated ? 'unauthenticated' : 'authenticated';

  const testType = labelToTestType(pipeline.testType);

  const credId = String(pipeline.credentialsId ?? '').trim();
  const credName = String(pipeline.credentials ?? '').trim();
  const hasCred = Boolean(credId);

  const vpnPid =
    pipeline.vpnProfileId != null && String(pipeline.vpnProfileId).trim() !== ''
      ? String(pipeline.vpnProfileId).trim()
      : null;

  const loginUrlRaw = String(pipeline.loginUrl ?? '').trim();

  const networkType =
    vpnPid || String(assetExposure ?? '').toLowerCase() === 'internal' ? 'internal' : 'external';

  const th = pipeline.throttlingSetting && typeof pipeline.throttlingSetting === 'object'
    ? pipeline.throttlingSetting
    : {};
  const rateLimit = th.rateLimit ?? th.rate_limit ?? 80;
  const concurrency = th.concurrency ?? 10;
  const requestTimeout = th.requestTimeout ?? th.request_timeout ?? 5;
  const vulnRetries = th.retries ?? 2;
  const strategyRaw = String(th.strategy ?? '').trim().toLowerCase();
  const strategy = ['stealth', 'standard', 'turbo', 'custom'].includes(strategyRaw)
    ? strategyRaw
    : 'standard';

  const config_settings = {
    vulnerability: {
      rate_limit: rateLimit,
      concurrency,
      timeout: requestTimeout,
      retries: vulnRetries,
    },
    validation: {
      timeout: requestTimeout,
      retries: vulnRetries,
    },
    /** Matches `config_settings.strategy` from ThrottlingSetup / persistPipelineGenptFromScheduleWizard */
    strategy,
  };

  const continueAsUnauth =
    authType === 'unauthenticated'
      ? true
      : pipeline.continueAsUnauth !== false;

  const submitted = {
    targetURL,
    assetId,
    testType,
    authType,
    showAuthFields: authType === 'authenticated',
    checked: authType === 'authenticated',
    continueAsUnauth,
    githubAccount: null,
    githubRepo: null,
    networkType,
    selectedVpnProfile: vpnPid,
    savedCredential: hasCred ? credId : null,
    ...(hasCred ? { savedCredentialName: credName || credId } : {}),
    loginUrl: loginUrlRaw || null,
    config_settings,
    // ROAR-4511: carry the restored Business Scenario through so TestSetup rehydrates it confirmed
    // (TestSetup.js:179). scenario_id is the persisted key; detail is merged onto pipeline upstream.
    ...(testType === 'scenario' && {
      scenarioId: pipeline.scenarioId || null,
      scenarioDescription: pipeline.scenarioDescription || '',
      featureName: pipeline.featureName || '',
      scenarioSteps: Array.isArray(pipeline.scenarioSteps) ? pipeline.scenarioSteps : [],
    }),
  };

  // Collection source is URL or file (never both). Restore whichever was saved so the widget
  // shows the previously-configured collection and the modal's "move to step 2" gate is satisfied.
  const apiUrl = String(pipeline.apiCollectionURL ?? '').trim();
  if (apiUrl) {
    submitted.api_collection_urls = [apiUrl];
  } else {
    const apiFiles = Array.isArray(pipeline.apiCollectionFiles)
      ? pipeline.apiCollectionFiles
          .map((f) => {
            const file_key = f?.file_key ?? f?.fileKey;
            if (!file_key) return null;
            const file_name = f?.file_name ?? f?.fileName;
            return file_name ? { file_key, file_name } : { file_key };
          })
          .filter(Boolean)
      : [];
    if (apiFiles.length > 0) {
      submitted.api_files = apiFiles;
    }
  }
  const schemaPath = String(pipeline.schemaEndpointPath ?? '').trim();
  if (schemaPath) {
    submitted.schema_endpoint_url = schemaPath;
  }

  // Authenticated API tests: restore the saved cookie + custom header pairs so the
  // Authentication Details fields repopulate on re-open (parity with api collection above).
  if (authType === 'authenticated') {
    const cookieAuth = String(pipeline.cookieAuthentication ?? '').trim();
    if (cookieAuth) {
      submitted.cookieAuthentication = cookieAuth;
    }
    const headers = Array.isArray(pipeline.customHeaders)
      ? pipeline.customHeaders
          .map((h) => ({
            headerName: String(h?.headerName ?? h?.header_name ?? '').trim(),
            headerValue: String(h?.headerValue ?? h?.header_value ?? '').trim(),
          }))
          .filter((h) => h.headerName && h.headerValue)
      : [];
    if (headers.length > 0) {
      submitted.customHeaders = headers;
    }
  }

  return {
    submittedData: submitted,
    throttlingData: config_settings,
  };
}

export default mapPipelineConfigToGenptEditPrefill;
